// src/lib/coverage/deriveScores.ts
// ============================================================
// SCORING FORMULAS — parsed workbook rows to measure scores on the native 1 to 5 scale
//
// This is what makes an upload produce a real score change rather than a scripted one.
// Every number below is computed from the dropped file. Nothing is looked up, nothing is
// nudged toward a nicer figure, and there are no floors.
//
// A LOW SCORE FROM A REAL INVENTORY IS CORRECT BEHAVIOUR. Measure 46 against the ACME
// example workbook lands at 1.6 because that inventory genuinely reports 2.1 unallocated
// FTE against 14.0 total. Do not add a floor to make it read better. The whole claim of
// the coverage model is that the number came from the file.
//
// SCALE: every score is the native 1 to 5, clamped to [1, 5] and rounded to one decimal.
// Never converted to a percentage here.
//
// THE `basis` STRING IS LOAD-BEARING, not a debug note. It is what the override display
// shows a client when their document contradicts their survey answer, so it has to state
// the count the score came from: "2 of 9 platforms at real time or near real time" is
// checkable against their own file, and "the inventory scored 1.9" is not. The row noun
// follows the sheet's identifying column, which for D3 is "Platform or data domain".
//
// Mock layer only. Pure functions of their arguments.
// ============================================================

import type {
  BoardPriorityRow,
  DataAssetRow,
  GovernanceRow,
  StaffingRow,
} from './parseInventory';

// ---- shared helpers --------------------------------------------------------

const MIN_SCORE = 1;
const MAX_SCORE = 5;

/** Clamp to [1, 5] and round to one decimal. The only exit point for a score. */
export function toScore(raw: number): number {
  if (!Number.isFinite(raw)) return MIN_SCORE;
  const clamped = Math.min(MAX_SCORE, Math.max(MIN_SCORE, raw));
  return Math.round(clamped * 10) / 10;
}

/** A share in [0, 1] mapped onto the 1 to 5 scale. The house band. */
export function shareToScore(share: number): number {
  return toScore(1 + 4 * (Number.isFinite(share) ? share : 0));
}

/** One decimal, for reported totals such as FTE. Not a score. */
const round1 = (n: number) => Math.round(n * 10) / 10;

const lower = (s: string) => (s || '').toLowerCase();

/** "1 platform" / "9 platforms". Basis clauses read inside a sentence, so they must agree. */
const plural = (n: number, singular: string, pluralForm = `${singular}s`) =>
  `${n} ${n === 1 ? singular : pluralForm}`;

function containsAny(haystack: string, needles: string[]): boolean {
  const h = lower(haystack);
  return needles.some((n) => h.includes(n));
}

function mean(values: number[]): number {
  const usable = values.filter((v) => Number.isFinite(v));
  if (usable.length === 0) return NaN;
  return usable.reduce((a, b) => a + b, 0) / usable.length;
}

export interface DerivedScore {
  measureId: number;
  /** Null when the input could not produce a score. The measure keeps its prior tier. */
  score: number | null;
  /** True when the input covered the category but had nothing to say about this measure. */
  assumed: boolean;
  /** True when the value came from keyword inference rather than a declared column. */
  inferred: boolean;
  /** Short, user-facing explanation of where the number came from. */
  basis: string;
  /** Set only for per-opportunity measures. */
  opportunityId?: string;
}

const score = (
  measureId: number,
  value: number | null,
  basis: string,
  extra: Partial<DerivedScore> = {},
): DerivedScore => ({
  measureId,
  score: value,
  assumed: false,
  inferred: false,
  basis,
  ...extra,
});

// ============================================================
// D3 — Data_Asset_Inventory
// ============================================================

/** Substrings that count as a recognised interoperability standard, for measure 27. */
export const RECOGNISED_STANDARDS = ['hl7', 'fhir', 'x12', 'naaccr', 'uscdi'];

/** Substrings that count as connectivity, for measure 30. Scanned across the whole row. */
const CONNECTIVITY_TERMS = ['hl7', 'fhir', 'exchange', 'hie'];

/** Substrings that mark a row as warehouse-resident, for measure 29. */
const WAREHOUSE_TERMS = ['warehouse', 'mart', 'caboodle'];

/** Refresh cadences that count as real time, for measure 32. */
const REAL_TIME_CADENCES = ['real time', 'near real time'];

/** Availability cell weights, for measure 28. Anything else counts as zero. */
function availabilityWeight(cell: string): number {
  const v = lower(cell).trim();
  if (v.startsWith('yes')) return 1;
  if (v.startsWith('partial')) return 0.5;
  return 0;
}

/** Historical depth in years, banded. Applied to the org mean, not per row. */
export function bandHistoricalYears(years: number): number {
  if (!Number.isFinite(years)) return MIN_SCORE;
  if (years < 2) return 1;
  if (years < 4) return 2;
  if (years < 6) return 3;
  if (years < 10) return 4;
  return 5;
}

/** Whole-row text, for the scans that are not confined to one column. */
const rowText = (r: DataAssetRow) =>
  [r.asset, r.platform, r.standard, r.availability, r.refreshCadence].join(' ');

export interface DataAssetSummary {
  rowCount: number;
  meanQuality: number;
  standardsRecognised: number;
  realTimeRows: number;
}

export function summariseDataAssets(rows: DataAssetRow[]): DataAssetSummary {
  return {
    rowCount: rows.length,
    meanQuality: round1(mean(rows.map((r) => r.quality))),
    standardsRecognised: rows.filter((r) => containsAny(r.standard, RECOGNISED_STANDARDS)).length,
    realTimeRows: rows.filter((r) => containsAny(r.refreshCadence, REAL_TIME_CADENCES)).length,
  };
}

/** D3 measures: 26, 27, 28, 29, 30, 31, 32. */
export function deriveDataAssetScores(rows: DataAssetRow[]): DerivedScore[] {
  const total = rows.length;
  if (total === 0) return [];

  const qualities = rows.map((r) => r.quality);
  const orgMean = mean(qualities);

  // 26 — mean of the self-rated quality column.
  const m26 = score(
    26,
    toScore(orgMean),
    `a mean self-rated quality of ${round1(orgMean)} across ${plural(total, 'platform')}`,
  );

  // 27 — share of rows naming a recognised standard. Confined to the standard column,
  // which is the column that carries the claim.
  const recognised = rows.filter((r) => containsAny(r.standard, RECOGNISED_STANDARDS)).length;
  const m27 = score(
    27,
    shareToScore(recognised / total),
    `${recognised} of ${total} platforms name a recognised standard`,
  );

  // 28 — availability, with Partially counting half.
  const availability = rows.reduce((sum, r) => sum + availabilityWeight(r.availability), 0);
  const fullyAvailable = rows.filter((r) => availabilityWeight(r.availability) === 1).length;
  const partlyAvailable = rows.filter((r) => availabilityWeight(r.availability) === 0.5).length;
  const m28 = score(
    28,
    shareToScore(availability / total),
    `${fullyAvailable} of ${total} platforms fully available to the analytics team and ${partlyAvailable} partially`,
  );

  // 29 — mean quality of warehouse-resident rows. Falls back to org mean minus 0.5 when the
  // inventory names no warehouse at all, because "no warehouse" is worse than the average,
  // not unknown.
  //
  // Scans the asset AND platform cells, which is what "platform or vendor" means against
  // the shipped D3 layout: its two identifying columns are "Platform or data domain" and
  // "Vendor or product", so the platform name lands in `asset` and the vendor in
  // `platform`. Reading only one of them missed the row named "Enterprise data warehouse".
  const warehouseRows = rows.filter((r) => containsAny(`${r.asset} ${r.platform}`, WAREHOUSE_TERMS));
  const m29 = warehouseRows.length
    ? score(
        29,
        toScore(mean(warehouseRows.map((r) => r.quality))),
        `a mean quality of ${round1(mean(warehouseRows.map((r) => r.quality)))} across ${plural(
          warehouseRows.length,
          'warehouse-resident platform',
        )}`,
      )
    : score(29, toScore(orgMean - 0.5), 'no warehouse-resident platform named, so the org mean less 0.5');

  // 30 — connectivity terms, scanned across the whole row: an exchange or HIE shows up in
  // the platform or asset name, not in the standard column.
  const connected = rows.filter((r) => containsAny(rowText(r), CONNECTIVITY_TERMS)).length;
  const m30 = score(
    30,
    shareToScore(connected / total),
    `${connected} of ${total} platforms carry an exchange or interoperability standard`,
  );

  // 31 — mean historical depth, banded.
  const meanYears = mean(rows.map((r) => r.historicalYears));
  const m31 = score(
    31,
    toScore(bandHistoricalYears(meanYears)),
    `a mean historical depth of ${round1(meanYears)} years across ${plural(total, 'platform')}`,
  );

  // 32 — share of rows on a real time or near real time cadence.
  const realTime = rows.filter((r) => containsAny(r.refreshCadence, REAL_TIME_CADENCES)).length;
  const m32 = score(
    32,
    shareToScore(realTime / total),
    `${realTime} of ${total} platforms at real time or near real time`,
  );

  return [m26, m27, m28, m29, m30, m31, m32];
}

// ============================================================
// D1 — Staffing_Capacity
// ============================================================

/** Team or skill terms that count as technical capacity, for measure 42. */
const TECHNICAL_TERMS = ['engineering', 'infrastructure', 'integration'];

/** What a per-opportunity measure needs to be computable. */
export interface OpportunityRequirement {
  opportunityId: string;
  label?: string;
  /** Required skill areas. Absent or empty leaves measure 43 unscored. */
  requiredSkillAreas?: string[];
  /** Required FTE. Absent leaves measure 51 unscored. */
  requiredFte?: number;
}

export interface StaffingSummary {
  rowCount: number;
  totalFte: number;
  unallocatedFte: number;
  technicalFte: number;
}

export function summariseStaffing(rows: StaffingRow[]): StaffingSummary {
  const finite = (n: number) => (Number.isFinite(n) ? n : 0);
  const totalFte = rows.reduce((s, r) => s + finite(r.totalFte), 0);
  const unallocatedFte = rows.reduce((s, r) => s + finite(r.unallocatedFte), 0);
  const technicalFte = rows
    .filter((r) => containsAny(`${r.team} ${r.skills}`, TECHNICAL_TERMS))
    .reduce((s, r) => s + finite(r.totalFte), 0);
  return {
    rowCount: rows.length,
    totalFte: round1(totalFte),
    unallocatedFte: round1(unallocatedFte),
    technicalFte: round1(technicalFte),
  };
}

/** Capacity gap in FTE, banded. Measure 51. */
export function bandCapacityGap(gap: number): number {
  if (!Number.isFinite(gap)) return MIN_SCORE;
  if (gap <= 0) return 5;
  if (gap <= 1) return 4;
  if (gap <= 2) return 3;
  if (gap <= 4) return 2;
  return 1;
}

/**
 * D1 measures. 46 and 42 are org level. 43 and 51 are per opportunity and are only
 * produced when an opportunity requirement is supplied.
 */
export function deriveStaffingScores(
  rows: StaffingRow[],
  opportunities: OpportunityRequirement[] = [],
): DerivedScore[] {
  if (rows.length === 0) return [];
  const summary = summariseStaffing(rows);
  const out: DerivedScore[] = [];

  // 46 — unallocated share of total FTE. No floor. See the header note.
  if (summary.totalFte > 0) {
    out.push(
      score(
        46,
        shareToScore(summary.unallocatedFte / summary.totalFte),
        `${summary.unallocatedFte} of ${summary.totalFte} FTE unallocated`,
      ),
    );
  }

  // 42 — technical share of total FTE, on the same band.
  if (summary.totalFte > 0) {
    out.push(
      score(
        42,
        shareToScore(summary.technicalFte / summary.totalFte),
        `${summary.technicalFte} of ${summary.totalFte} FTE in engineering, infrastructure or integration roles`,
      ),
    );
  }

  const allSkills = rows.map((r) => `${r.team} ${r.skills}`).join(' ');

  opportunities.forEach((opp) => {
    const required = opp.requiredSkillAreas ?? [];

    // 43 — required skill areas matched. Null when the opportunity carries no list, which
    // leaves the measure on whatever tier already held it.
    if (required.length > 0) {
      const matched = required.filter((skill) => containsAny(allSkills, [lower(skill)])).length;
      out.push(
        score(43, shareToScore(matched / required.length), `${matched} of ${required.length} required skill areas present`, {
          opportunityId: opp.opportunityId,
        }),
      );
    } else {
      out.push(
        score(43, null, 'No required skill areas listed for this opportunity', {
          opportunityId: opp.opportunityId,
        }),
      );
    }

    // 51 — capacity gap against unallocated FTE.
    if (Number.isFinite(opp.requiredFte as number)) {
      const gap = round1((opp.requiredFte as number) - summary.unallocatedFte);
      out.push(
        score(51, toScore(bandCapacityGap(gap)), gap <= 0
          ? `unallocated capacity of ${summary.unallocatedFte} FTE against the ${opp.requiredFte} FTE required`
          : `a shortfall of ${gap} FTE against the ${opp.requiredFte} FTE required`, {
          opportunityId: opp.opportunityId,
        }),
      );
    } else {
      out.push(
        score(51, null, 'No required FTE listed for this opportunity', {
          opportunityId: opp.opportunityId,
        }),
      );
    }
  });

  return out;
}

// ============================================================
// D2 — Board_Priorities
// ============================================================

export const BOARD_THEMES = [
  'Mission and Access',
  'Market Position',
  'Value-Based Care',
  'Growth and Viability',
  'Operational Efficiency',
  'Technology',
  'Revenue Diversification',
] as const;

export type BoardTheme = (typeof BOARD_THEMES)[number];

/** Theme to measure. Every Strategic Value measure has exactly one theme. */
export const THEME_TO_MEASURE: Readonly<Record<BoardTheme, number>> = {
  'Mission and Access': 5,
  'Market Position': 6,
  'Value-Based Care': 7,
  'Growth and Viability': 8,
  'Operational Efficiency': 21,
  Technology: 22,
  'Revenue Diversification': 24,
};

/**
 * Keyword inference, used ONLY when the theme column is empty across every row. Marked
 * inferred:true on the result so the surface can say so. Never a silent guess.
 */
const THEME_KEYWORDS: Readonly<Record<BoardTheme, string[]>> = {
  'Mission and Access': ['access', 'underserved', 'community', 'equity', 'mission', 'charity'],
  'Market Position': ['market share', 'competitive', 'differentiat', 'brand', 'market position'],
  'Value-Based Care': ['value-based', 'value based', 'risk contract', 'aco', 'shared savings', 'population health'],
  'Growth and Viability': ['growth', 'expand', 'volume', 'service line', 'viability', 'margin'],
  'Operational Efficiency': ['efficien', 'throughput', 'length of stay', 'readmission', 'cost per', 'productivity'],
  Technology: ['technology', 'platform', 'digital', 'analytics', 'modernize', 'modernise', 'ehr'],
  'Revenue Diversification': ['diversif', 'new revenue', 'employer', 'payer contract', 'joint venture', 'ancillary'],
};

/** Funding bonus per cell. The theme takes the MAX across its priorities. */
export function fundedBonus(cell: string): number {
  const v = lower(cell).trim();
  if (v.startsWith('yes')) return 1;
  if (v.startsWith('partial')) return 0.5;
  return 0;
}

/** Match a priority to a theme by keyword. Null when nothing matches. */
export function inferTheme(priorityText: string): BoardTheme | null {
  const t = lower(priorityText);
  for (const theme of BOARD_THEMES) {
    if (THEME_KEYWORDS[theme].some((k) => t.includes(k))) return theme;
  }
  return null;
}

/** Normalised theme match, so "value based care" and "Value-Based Care" agree. */
function matchDeclaredTheme(cell: string): BoardTheme | null {
  const norm = (s: string) => lower(s).replace(/[^a-z0-9]+/g, ' ').trim();
  const target = norm(cell);
  if (!target) return null;
  return BOARD_THEMES.find((t) => norm(t) === target) ?? null;
}

/**
 * D2 measures: the seven Strategic Value measures.
 *
 * score = 1 + 2 x (priorities on that theme) + funded bonus, clamped to 5.
 * A theme with zero priorities gets NO score. It returns assumed:true and the measure
 * stays on library default, because silence in a board document is not evidence of a low
 * priority, only evidence that this document did not speak to it.
 */
export function deriveBoardPriorityScores(rows: BoardPriorityRow[]): DerivedScore[] {
  if (rows.length === 0) return [];

  const themeColumnEmpty = rows.every((r) => r.theme.trim() === '');
  const inferred = themeColumnEmpty;

  const buckets = new Map<BoardTheme, BoardPriorityRow[]>();
  rows.forEach((r) => {
    const theme = inferred ? inferTheme(r.priority) : matchDeclaredTheme(r.theme);
    if (!theme) return;
    const list = buckets.get(theme);
    if (list) list.push(r);
    else buckets.set(theme, [r]);
  });

  return BOARD_THEMES.map((theme) => {
    const measureId = THEME_TO_MEASURE[theme];
    const priorities = buckets.get(theme) ?? [];

    if (priorities.length === 0) {
      return {
        measureId,
        score: null,
        assumed: true,
        inferred,
        basis: `no board priority on ${theme}`,
      };
    }

    const bonus = priorities.reduce((max, r) => Math.max(max, fundedBonus(r.funded)), 0);
    const raw = 1 + 2 * priorities.length + bonus;
    return {
      measureId,
      score: toScore(raw),
      assumed: false,
      inferred,
      basis: `${plural(priorities.length, 'board priority', 'board priorities')} on ${theme}`,
    };
  });
}

// ============================================================
// D4 — Governance_Charter
// ============================================================

/**
 * Control area -> measure, matched on the TEXT of the domain cell.
 *
 * MATCHED BY TEXT, NOT BY ROW POSITION. A client will reorder rows, omit the areas they
 * have nothing to say about, and rename them slightly. Position would silently attribute
 * one area's maturity to another measure, which is the worst failure available here: the
 * number would look plausible and be about the wrong thing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ORDER IS BEHAVIOUR. The first entry whose keyword appears in the domain wins, so this
 * array is not a lookup table with an arbitrary order; it is a precedence list, and moving
 * a row changes what a client's charter scores.
 *
 * The collisions it exists to resolve, each one PINNED in governanceCharter.test.ts rather
 * than trusted to this comment:
 *
 *   "Security and privacy audit"    -> 34, not 38. The row describes a security programme
 *                                      that happens to be audited, not the audit function.
 *   "Vendor security review"        -> 39, not 34. Third-party assurance, not the client's
 *                                      own security posture.
 *   "Consent and privacy"           -> 41, not 34. A consent row is about consent.
 *   "Legal agreements with vendors" -> 37, not 39. The subject is the agreement.
 *   "Data governance council"       -> 44, and nothing earlier may claim it.
 *
 * An earlier version had 38 above 34 and resolved the first case the wrong way, with a
 * comment asserting the opposite of what the code did. That is exactly why the ordering is
 * now tested: a comment cannot fail.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const GOVERNANCE_DOMAIN_MAP: { measureId: number; keywords: string[] }[] = [
  { measureId: 35, keywords: ['de-identification', 'de-identifi', 'deidentif', 'anonymi'] },
  { measureId: 36, keywords: ['regulatory framework', 'framework alignment'] },
  // Before 39: "legal agreements with vendors" is about the agreement.
  { measureId: 37, keywords: ['legal', 'contractual', 'agreement'] },
  // Before 34: "vendor security review" is third-party assurance, not own posture.
  { measureId: 39, keywords: ['third party', 'third-party', 'vendor'] },
  { measureId: 40, keywords: ['jurisdiction', 'multi-state', 'multi state', 'cross-border'] },
  // Before 34: "consent and privacy" is a consent row.
  { measureId: 41, keywords: ['consent'] },
  // Before 38: "security and privacy audit" describes the programme, not the audit.
  { measureId: 34, keywords: ['security', 'privacy'] },
  { measureId: 38, keywords: ['audit', 'reporting'] },
  // Last, and narrow on purpose. A bare 'governance' would match most rows on this sheet.
  { measureId: 44, keywords: ['intake', 'analytics governance', 'governance council', 'data governance'] },
];

/** Every measure the charter can inform, in catalog order. */
export const GOVERNANCE_MEASURE_IDS = [...GOVERNANCE_DOMAIN_MAP]
  .map((m) => m.measureId)
  .sort((a, b) => a - b);

/** The measure a control area maps to, or null when nothing matches. */
export function measureForDomain(domain: string): number | null {
  const d = lower(domain);
  return GOVERNANCE_DOMAIN_MAP.find((m) => m.keywords.some((k) => d.includes(k)))?.measureId ?? null;
}

export interface GovernanceSummary {
  rowCount: number;
  meanMaturity: number;
  /** Control areas that matched no measure. Reported rather than silently dropped. */
  unmatchedDomains: string[];
}

export function summariseGovernance(rows: GovernanceRow[]): GovernanceSummary {
  return {
    rowCount: rows.length,
    meanMaturity: round1(mean(rows.map((r) => r.maturity))),
    unmatchedDomains: rows.filter((r) => measureForDomain(r.domain) === null).map((r) => r.domain),
  };
}

/**
 * D4 measures. The score IS the row's maturity, already on the 1 to 5 scale, so nothing is
 * banded or rescaled: the client rated it and that rating is what shows.
 *
 * A measure with no matching control area returns assumed:true and no score, the same as a
 * D2 theme with no priority. A charter that lists eight areas and not the ninth has said
 * something about the ninth, and saying so is more useful than scoring it at a default.
 */
export function deriveGovernanceScores(rows: GovernanceRow[]): DerivedScore[] {
  if (rows.length === 0) return [];

  const byMeasure = new Map<number, GovernanceRow>();
  rows.forEach((r) => {
    const id = measureForDomain(r.domain);
    // First row wins for a measure, so a duplicated area cannot overwrite the first answer.
    if (id !== null && !byMeasure.has(id)) byMeasure.set(id, r);
  });

  return GOVERNANCE_MEASURE_IDS.map((measureId) => {
    const row = byMeasure.get(measureId);
    if (!row || !Number.isFinite(row.maturity)) {
      return {
        measureId,
        score: null,
        assumed: true,
        inferred: false,
        basis: 'No control area on the charter covers this',
      };
    }
    return {
      measureId,
      score: toScore(row.maturity),
      assumed: false,
      inferred: false,
      basis: `a self-rated maturity of ${toScore(row.maturity)} on ${row.domain.toLowerCase()}`,
    };
  });
}

// ============================================================
// Dispatch
// ============================================================

export type DeriveInput =
  | { template: 'dataAssets'; rows: DataAssetRow[] }
  | { template: 'boardPriorities'; rows: BoardPriorityRow[] }
  | { template: 'governanceCharter'; rows: GovernanceRow[] }
  | { template: 'staffing'; rows: StaffingRow[]; opportunities?: OpportunityRequirement[] };

/** Route parsed rows to the right formula set. */
export function deriveScores(input: DeriveInput): DerivedScore[] {
  if (input.template === 'dataAssets') return deriveDataAssetScores(input.rows);
  if (input.template === 'boardPriorities') return deriveBoardPriorityScores(input.rows);
  if (input.template === 'governanceCharter') return deriveGovernanceScores(input.rows);
  return deriveStaffingScores(input.rows, input.opportunities ?? []);
}
